import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { pluralize } from "@common/helpers/string";
import { ArrowDownIcon } from "@icons";
import { VStack } from "@design-system/VStack/VStack";
import { AccrualActivityList } from "./AccrualActivityList";
import {
    AccrualActivityResourceType,
    getAccrualEntityLabel,
    useAccrualActivity,
} from "./useAccrualActivity";
import styles from "./AccrualActivity.module.scss";

interface AccrualActivityProps {
    resourceType: AccrualActivityResourceType;
    resourceId: string;
    entityId: number;
    className?: string;
}

/**
 * @purpose Standalone activity section: owns its own "Activity" header, the
 * expand/collapse state and the change count, and hides itself entirely when
 * the document has no audit log. Inside an accordion use
 * `AccrualActivityPanel`, which lets the accordion own that chrome.
 */
export const AccrualActivity: React.FC<AccrualActivityProps> = ({
    resourceType,
    resourceId,
    entityId,
    className,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const toggle = useCallback(() => setIsExpanded((prev) => !prev), []);
    const activity = useAccrualActivity({
        resourceType,
        resourceId,
        isEnabled: isExpanded,
    });
    const { logs, totalCount, hasMore, isSuccess } = activity;

    if (isSuccess && totalCount === 0) {
        return null;
    }

    const buildCountLabel = () => {
        if (!isSuccess) {
            return null;
        }
        if (hasMore) {
            return `Showing ${logs.length} of ${totalCount} ${pluralize(
                "change",
                totalCount,
            )}`;
        }
        return `${totalCount} ${pluralize("change", totalCount)}`;
    };

    const countLabel = buildCountLabel();

    return (
        <VStack
            as="section"
            gap={12}
            className={className}
            data-testid={`accrual-activity-${getAccrualEntityLabel(
                resourceType,
            )}`}
        >
            <button
                type="button"
                className={styles.toggle}
                onClick={toggle}
                aria-expanded={isExpanded}
                aria-controls={`accrual-activity-list-${resourceId}`}
            >
                <span className={styles.heading}>Activity</span>
                <span className={styles.toggleMeta}>
                    {countLabel && (
                        <span className={styles.count}>{countLabel}</span>
                    )}
                    <ArrowDownIcon
                        className={classNames(styles.caret, {
                            [styles.caretExpanded]: isExpanded,
                        })}
                    />
                </span>
            </button>
            {isExpanded && (
                <AccrualActivityList
                    activity={activity}
                    resourceType={resourceType}
                    resourceId={resourceId}
                    entityId={entityId}
                />
            )}
        </VStack>
    );
};
